import { useNode, useEditor } from "@craftjs/core";
import React, { useState, useRef } from "react";
import { useResponsive } from "../../hooks/useResponsive";
import { getResponsiveFontSize, fontWeights, FontWeight } from "../../config/styleConfig";
import { selectedOutlineSx } from "../../config/settingsStyles";
import { Box, TextField, Select, MenuItem, FormControl, InputLabel, Slider, Typography, FormControlLabel, Checkbox, Tabs, Tab } from "@mui/material";
import { useLanguage, getLocalizedText } from "../../context/LanguageContext";

export interface TextProps {
  text: string | Record<string, string>;
  fontSize?: number;
  mobileFontSize?: number;
  fontFamily?: string;
  fontWeight?: "normal" | "medium" | "semibold" | "bold";
  fontStyle?: "normal" | "italic";
  textAlign?: "left" | "center" | "right";
  color?: string;
  tagName?: "p" | "h1" | "h2" | "h3" | "h4" | "span";
  lineHeight?: number;
  marginTop?: number;
  marginBottom?: number;
  marginLeft?: number;
  marginRight?: number;
  whiteSpace?: string;
}

// Use shared fontWeights from styleConfig

export const Text = ({
  text = "Enter text here",
  fontSize = 16,
  mobileFontSize,
  fontFamily,
  fontWeight = "normal",
  fontStyle = "normal",
  textAlign = "left",
  color = "#000000",
  tagName = "p",
  lineHeight = 1.5,
  marginTop = 0,
  marginBottom = 0,
  marginLeft = 0,
  marginRight = 0,
  whiteSpace,
}: TextProps) => {
  const {
    connectors: { connect, drag },
    selected,
    actions: { setProp },
  } = useNode((state) => ({
    selected: state.events.selected,
  }));

  const { enabled: editorEnabled } = useEditor((state) => state.options);
  const { currentLang } = useLanguage();

  const displayText = getLocalizedText(text, currentLang);

  const containerRef = useRef<HTMLDivElement>(null);
  const { isMobile } = useResponsive(containerRef as React.RefObject<HTMLElement>);

  const [isEditing, setIsEditing] = useState(false);
  const editRef = useRef<HTMLElement>(null);

  const handleDoubleClick = (e: React.MouseEvent) => {
    if (!editorEnabled) return;
    e.stopPropagation();
    setIsEditing(true);
    // Focus and select all text after React re-render
    setTimeout(() => {
      const el = editRef.current;
      if (el) {
        el.focus();
        const range = document.createRange();
        range.selectNodeContents(el);
        const sel = window.getSelection();
        sel?.removeAllRanges();
        sel?.addRange(range);
      }
    }, 0);
  };

  const handleBlur = () => {
    setIsEditing(false);
    const newText = editRef.current?.innerText || displayText;
    if (newText !== displayText) {
      setProp((props: TextProps) => {
        if (typeof props.text === "object") {
          props.text = { ...props.text, [currentLang]: newText };
        } else {
          props.text = newText;
        }
      });
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      (e.target as HTMLElement).blur();
    }
    if (e.key === "Escape") {
      if (editRef.current) editRef.current.innerText = displayText;
      (e.target as HTMLElement).blur();
    }
  };

  // Calculate responsive font sizes using shared config
  const safeFontSize = fontSize || 16;
  const safeMarginTop = marginTop || 0;
  const safeMarginBottom = marginBottom || 0;
  const safeMarginLeft = marginLeft || 0;
  const safeMarginRight = marginRight || 0;
  const safeFontWeight = (fontWeight || "normal") as FontWeight;

  // Use shared function for mobile font size calculation; mobileFontSize prop overrides on mobile
  const responsiveFontSize = (isMobile && mobileFontSize != null)
    ? mobileFontSize
    : getResponsiveFontSize(safeFontSize, isMobile);

  return (
    <Box
      component={tagName || "p"}
      ref={(ref: HTMLDivElement | null) => {
        if (ref) {
          connect(isEditing ? ref : drag(ref));
          (containerRef as React.MutableRefObject<HTMLDivElement | null>).current = ref;
          (editRef as React.MutableRefObject<HTMLElement | null>).current = ref;
        }
      }}
      onDoubleClick={handleDoubleClick}
      contentEditable={isEditing}
      suppressContentEditableWarning
      onBlur={isEditing ? handleBlur : undefined}
      onKeyDown={isEditing ? handleKeyDown : undefined}
      sx={{
        fontFamily: fontFamily
          ? `'${fontFamily}', var(--font-family), sans-serif`
          : /[\u0E00-\u0E7F]/.test(displayText) ? undefined : "var(--font-family-en, var(--font-family))",
        ...(tagName === "span" ? { display: "inline-block", m: 0, p: 0 } : {}),
        fontSize: responsiveFontSize,
        fontWeight: fontWeights[safeFontWeight],
        fontStyle: fontStyle || "normal",
        textAlign,
        ...(color?.startsWith("linear-gradient")
          ? {
              background: color,
              backgroundClip: "text",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              ...(fontStyle === "italic" ? { paddingRight: "0.25em" } : {}),
            }
          : { color }),
        lineHeight,
        ...(whiteSpace ? { whiteSpace } : { wordWrap: "break-word", overflowWrap: "break-word" }),
        mt: isMobile ? `${Math.max(0, safeMarginTop * 0.5)}px` : `${safeMarginTop}px`,
        mb: isMobile ? `${Math.max(0, safeMarginBottom * 0.5)}px` : `${safeMarginBottom}px`,
        ml: isMobile ? `${Math.max(0, safeMarginLeft * 0.5)}px` : `${safeMarginLeft}px`,
        mr: isMobile ? `${Math.max(0, safeMarginRight * 0.5)}px` : `${safeMarginRight}px`,
        ...(selected && selectedOutlineSx),
        ...(isEditing && {
          outline: "2px solid #1976d2",
          outlineOffset: "2px",
          cursor: "text",
          minWidth: "20px",
          "&:focus": { outline: "2px solid #1976d2" },
        }),
      }}
    >
      {displayText}
    </Box>
  );
};

const SG = ({ title, children }: { title: string; children: React.ReactNode }) => (
  <Box sx={{ border: "1px solid", borderColor: "divider", borderRadius: 2, overflow: "hidden" }}>
    <Box sx={{ px: 1.5, py: 1, bgcolor: "#f9fafb", borderBottom: "1px solid", borderColor: "divider" }}>
      <Typography variant="body2" fontWeight={600} color="text.secondary" sx={{ fontSize: "0.8rem", textTransform: "uppercase", letterSpacing: "0.5px" }}>{title}</Typography>
    </Box>
    <Box sx={{ p: 1.5, display: "flex", flexDirection: "column", gap: 1.5 }}>{children}</Box>
  </Box>
);

export const TextSettings = () => {
  const { currentLang, setCurrentLang } = useLanguage();
  const {
    actions: { setProp },
    text,
    fontSize,
    fontFamily,
    fontWeight,
    fontStyle,
    textAlign,
    color,
    tagName,
    lineHeight,
    marginTop,
    marginBottom,
    marginLeft,
    marginRight,
    whiteSpace,
  } = useNode((node) => ({
    text: node.data.props.text,
    fontSize: node.data.props.fontSize,
    fontFamily: node.data.props.fontFamily,
    fontWeight: node.data.props.fontWeight,
    fontStyle: node.data.props.fontStyle,
    textAlign: node.data.props.textAlign,
    color: node.data.props.color,
    tagName: node.data.props.tagName,
    lineHeight: node.data.props.lineHeight,
    marginTop: node.data.props.marginTop,
    marginBottom: node.data.props.marginBottom,
    marginLeft: node.data.props.marginLeft,
    marginRight: node.data.props.marginRight,
    whiteSpace: node.data.props.whiteSpace,
  }));

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      {/* Content */}
      <SG title="Content">
        <FormControlLabel
          control={
            <Checkbox
              size="small"
              checked={typeof text === "object"}
              onChange={(e) => {
                setProp((props: TextProps) => {
                  if (e.target.checked) {
                    const current = typeof props.text === "string" ? props.text : "";
                    props.text = { th: current, en: "" };
                  } else {
                    const obj = props.text as Record<string, string>;
                    props.text = obj?.th || obj?.en || "";
                  }
                });
              }}
            />
          }
          label={<Typography variant="caption">หลายภาษา (TH/EN)</Typography>}
        />
        {typeof text === "object" ? (
          <>
            <Tabs value={currentLang} onChange={(_, v) => setCurrentLang(v)} sx={{ minHeight: 32, "& .MuiTab-root": { minHeight: 32, py: 0.5, fontSize: "0.75rem" } }}>
              <Tab label="🇹🇭 TH" value="th" />
              <Tab label="🇺🇸 EN" value="en" />
            </Tabs>
            <TextField label={`Text (${currentLang.toUpperCase()})`} value={(text as Record<string, string>)[currentLang] || ""} multiline rows={3} size="small" fullWidth
              onChange={(e) => setProp((props: TextProps) => {
                props.text = { ...(props.text as Record<string, string>), [currentLang]: e.target.value };
              })} />
          </>
        ) : (
          <TextField label="Text Content" value={text} multiline rows={3} size="small" fullWidth
            onChange={(e) => setProp((props: TextProps) => (props.text = e.target.value))} />
        )}
        <FormControl size="small" fullWidth>
          <InputLabel>Tag</InputLabel>
          <Select value={tagName} label="Tag"
            onChange={(e) => setProp((props: TextProps) => (props.tagName = e.target.value as TextProps["tagName"]))}
          >
            <MenuItem value="p">Paragraph (p)</MenuItem>
            <MenuItem value="h1">Heading 1 (h1)</MenuItem>
            <MenuItem value="h2">Heading 2 (h2)</MenuItem>
            <MenuItem value="h3">Heading 3 (h3)</MenuItem>
            <MenuItem value="h4">Heading 4 (h4)</MenuItem>
            <MenuItem value="span">Span</MenuItem>
          </Select>
        </FormControl>
      </SG>

      {/* Font */}
      <SG title="Font">
        <FormControl size="small" fullWidth>
          <InputLabel>Font Family</InputLabel>
          <Select
            value={fontFamily || ""}
            label="Font Family"
            onChange={(e) => setProp((props: TextProps) => (props.fontFamily = e.target.value || undefined))}
          >
            <MenuItem value="">ตาม Theme</MenuItem>
            <MenuItem value="Sarabun" sx={{ fontFamily: "'Sarabun', sans-serif" }}>Sarabun (ไทย)</MenuItem>
            <MenuItem value="Prompt" sx={{ fontFamily: "'Prompt', sans-serif" }}>Prompt (ไทย)</MenuItem>
            <MenuItem value="Kanit" sx={{ fontFamily: "'Kanit', sans-serif" }}>Kanit (ไทย)</MenuItem>
            <MenuItem value="Noto Sans Thai" sx={{ fontFamily: "'Noto Sans Thai', sans-serif" }}>Noto Sans (ไทย)</MenuItem>
            <MenuItem value="Inter" sx={{ fontFamily: "'Inter', sans-serif" }}>Inter</MenuItem>
            <MenuItem value="Poppins" sx={{ fontFamily: "'Poppins', sans-serif" }}>Poppins</MenuItem>
            <MenuItem value="Fahkwang" sx={{ fontFamily: "'Fahkwang', sans-serif" }}>Fahkwang</MenuItem>
          </Select>
        </FormControl>
        <Box>
          <Typography variant="body2" gutterBottom>Font Size: {fontSize}px</Typography>
          <Slider value={fontSize} onChange={(_, v) => setProp((props: TextProps) => (props.fontSize = v as number))} min={10} max={72} size="small" valueLabelDisplay="auto" />
        </Box>
        <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1 }}>
          <FormControl size="small" fullWidth>
            <InputLabel>Weight</InputLabel>
            <Select value={fontWeight} label="Weight"
              onChange={(e) => setProp((props: TextProps) => (props.fontWeight = e.target.value as TextProps["fontWeight"]))}
            >
              <MenuItem value="normal">Normal</MenuItem>
              <MenuItem value="medium">Medium</MenuItem>
              <MenuItem value="semibold">Semibold</MenuItem>
              <MenuItem value="bold">Bold</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" fullWidth>
            <InputLabel>Style</InputLabel>
            <Select value={fontStyle} label="Style"
              onChange={(e) => setProp((props: TextProps) => (props.fontStyle = e.target.value as TextProps["fontStyle"]))}
            >
              <MenuItem value="normal">Normal</MenuItem>
              <MenuItem value="italic">Italic</MenuItem>
            </Select>
          </FormControl>
        </Box>
        <FormControl size="small" fullWidth>
          <InputLabel>Align</InputLabel>
          <Select value={textAlign} label="Align"
            onChange={(e) => setProp((props: TextProps) => (props.textAlign = e.target.value as TextProps["textAlign"]))}
          >
            <MenuItem value="left">Left</MenuItem>
            <MenuItem value="center">Center</MenuItem>
            <MenuItem value="right">Right</MenuItem>
          </Select>
        </FormControl>
        <Box>
          <Typography variant="body2" gutterBottom>Line Height: {lineHeight}</Typography>
          <Slider value={lineHeight} onChange={(_, v) => setProp((props: TextProps) => (props.lineHeight = v as number))} min={1} max={3} step={0.1} size="small" valueLabelDisplay="auto" />
        </Box>
        <FormControlLabel
          control={<Checkbox checked={whiteSpace === "nowrap"} onChange={(e) => setProp((props: TextProps) => (props.whiteSpace = e.target.checked ? "nowrap" : undefined))} size="small" />}
          label={<Typography variant="body2">ไม่ตัดบรรทัด</Typography>}
        />
      </SG>

      {/* Color */}
      <SG title="Color">
        {color?.startsWith("linear-gradient") ? (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
              <Box component="input" type="color"
                value={(() => { const m = color.match(/#[0-9a-fA-F]{6}/g); return m?.[0] || "#F2819F"; })()}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  const m = color.match(/#[0-9a-fA-F]{6}/g); const c2 = m?.[1] || "#FAAF40"; const deg = color.match(/(\d+)deg/)?.[1] || "90";
                  setProp((props: TextProps) => (props.color = `linear-gradient(${deg}deg, ${e.target.value} 0%, ${c2} 100%)`));
                }}
                sx={{ width: 48, height: 36, borderRadius: 1, border: "1px solid", borderColor: "divider", cursor: "pointer" }}
              />
              <Typography variant="body2">→</Typography>
              <Box component="input" type="color"
                value={(() => { const m = color.match(/#[0-9a-fA-F]{6}/g); return m?.[1] || "#FAAF40"; })()}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  const m = color.match(/#[0-9a-fA-F]{6}/g); const c1 = m?.[0] || "#F2819F"; const deg = color.match(/(\d+)deg/)?.[1] || "90";
                  setProp((props: TextProps) => (props.color = `linear-gradient(${deg}deg, ${c1} 0%, ${e.target.value} 100%)`));
                }}
                sx={{ width: 48, height: 36, borderRadius: 1, border: "1px solid", borderColor: "divider", cursor: "pointer" }}
              />
            </Box>
            <FormControl size="small" fullWidth>
              <InputLabel>ทิศทาง</InputLabel>
              <Select value={color.match(/(\d+)deg/)?.[1] || "90"} label="ทิศทาง"
                onChange={(e) => { const m = color.match(/#[0-9a-fA-F]{6}/g); setProp((props: TextProps) => (props.color = `linear-gradient(${e.target.value}deg, ${m?.[0] || "#F2819F"} 0%, ${m?.[1] || "#FAAF40"} 100%)`)); }}
              >
                <MenuItem value="0">บนลงล่าง (0°)</MenuItem>
                <MenuItem value="90">ซ้ายไปขวา (90°)</MenuItem>
                <MenuItem value="180">ล่างขึ้นบน (180°)</MenuItem>
                <MenuItem value="270">ขวาไปซ้าย (270°)</MenuItem>
                <MenuItem value="45">เฉียง (45°)</MenuItem>
              </Select>
            </FormControl>
            <FormControlLabel
              control={<Checkbox checked onChange={() => setProp((props: TextProps) => (props.color = "#000000"))} size="small" />}
              label={<Typography variant="body2">สีไล่ระดับ (Gradient)</Typography>}
            />
          </Box>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            <Box component="input" type="color" value={color || "#000000"}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setProp((props: TextProps) => (props.color = e.target.value))}
              sx={{ width: "100%", height: 40, borderRadius: 1, border: "1px solid", borderColor: "divider", cursor: "pointer" }}
            />
            <FormControlLabel
              control={<Checkbox checked={false} onChange={() => setProp((props: TextProps) => (props.color = "linear-gradient(90deg, #F2819F 0%, #FAAF40 100%)"))} size="small" />}
              label={<Typography variant="body2">สีไล่ระดับ (Gradient)</Typography>}
            />
          </Box>
        )}
      </SG>

      {/* Spacing */}
      <SG title="Margin">
        <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1 }}>
          <TextField label="Top" type="number" value={marginTop} size="small"
            onChange={(e) => setProp((props: TextProps) => (props.marginTop = parseInt(e.target.value) || 0))} />
          <TextField label="Bottom" type="number" value={marginBottom} size="small"
            onChange={(e) => setProp((props: TextProps) => (props.marginBottom = parseInt(e.target.value) || 0))} />
          <TextField label="Left" type="number" value={marginLeft} size="small"
            onChange={(e) => setProp((props: TextProps) => (props.marginLeft = parseInt(e.target.value) || 0))} />
          <TextField label="Right" type="number" value={marginRight} size="small"
            onChange={(e) => setProp((props: TextProps) => (props.marginRight = parseInt(e.target.value) || 0))} />
        </Box>
      </SG>
    </Box>
  );
};

Text.craft = {
  displayName: "Text",
  props: {
    text: "Enter text here",
    fontSize: 16,
    fontFamily: undefined,
    fontWeight: "normal",
    fontStyle: "normal",
    textAlign: "left",
    color: "#000000",
    tagName: "p",
    lineHeight: 1.5,
    marginTop: 0,
    marginBottom: 0,
    marginLeft: 0,
    marginRight: 0,
    whiteSpace: undefined,
  },
  related: {
    settings: TextSettings,
  },
};
